import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import useEnvironmentsQuery from './useEnvironmentsQuery';
import { EnvironmentRef } from '@hud/portal-dtos/webapp-common.dto';
import useAnalytics from './useAnalytics';
import { useGlobalFilter } from './useGlobalFilter';

// Define the shape of the context data for TypeScript (optional)
interface EnvironmentContextType {
  environment: EnvironmentRef | null;
  environments: EnvironmentRef[];
  setEnvironment: (environment: EnvironmentRef) => void;
}

// Create a context with a default value (will be overridden by the provider)
const EnvironmentContext = createContext<EnvironmentContextType | undefined>(
  undefined,
);

// Provider component
export const EnvironmentProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [environmentName, setEnvironmentName] = useGlobalFilter('environment');

  const environmentsQuery = useEnvironmentsQuery();
  const analytics = useAnalytics();

  const sortedEnvironments = useMemo(() => {
    if (!environmentsQuery.isSuccess) {
      return [];
    }
    const environments = environmentsQuery.data;
    const production = environments.find((env) => env.name === 'production');
    const staging = environments.find((env) => env.name === 'staging');
    const local = environments.find((env) => env.name === 'local');
    const rest = environments.filter(
      (env) =>
        env.name !== 'production' &&
        env.name !== 'staging' &&
        env.name !== 'local',
    );
    return [production, staging, ...rest, local].filter(
      Boolean,
    ) as EnvironmentRef[];
  }, [environmentsQuery]);

  const environment = useMemo<EnvironmentRef | null>(() => {
    if (!sortedEnvironments.length) {
      return null;
    }
    return (
      sortedEnvironments.find((env) => env.name === environmentName) ??
      sortedEnvironments[0]
    );
  }, [sortedEnvironments, environmentName]);

  useEffect(() => {
    if (environmentsQuery.isSuccess) {
      if (!environmentName) {
        const productionEnv = environmentsQuery.data.find(
          (env) => env.name === 'production',
        );
        const envWithMostFunctions = environmentsQuery.data.reduce(
          (prev, current) =>
            prev.totalFunctions > current.totalFunctions ? prev : current,
        );
        const defaultEnvironment =
          productionEnv?.totalFunctions && productionEnv.totalFunctions > 0
            ? productionEnv
            : envWithMostFunctions;

        setEnvironmentName(defaultEnvironment.name);
      }
    }
  }, [
    environmentsQuery.isSuccess,
    environmentsQuery.data,
    environmentName,
    analytics,
    setEnvironmentName,
  ]);

  const handleSetEnvironment = useCallback(
    (environment: EnvironmentRef) => {
      const previousEnvironmentName = environmentName;
      setEnvironmentName(environment.name);
      analytics.capture('hud_change_environment_clicked', {
        environment: environment.name,
        previous_environment: previousEnvironmentName ?? undefined,
      });
    },
    [analytics, environmentName, setEnvironmentName],
  );

  const contextValue = useMemo(
    () => ({
      environment,
      environments: sortedEnvironments,
      setEnvironment: handleSetEnvironment,
    }),
    [environment, sortedEnvironments, handleSetEnvironment],
  );

  return (
    <EnvironmentContext.Provider value={contextValue}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export default function useEnvironment(): EnvironmentContextType {
  const context = useContext(EnvironmentContext);
  if (context === undefined) {
    throw new Error(
      'useEnvironment must be used within an EnvironmentProvider',
    );
  }
  return context;
}
